import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/hooks/useAuth';

const GOOGLE_ERROR_MESSAGES: Record<string, string> = {
  google_failed: 'Google sign-in failed. Please try again.',
  google_cancelled: 'Google sign-in was cancelled.',
  google_not_registered: 'No OchrePoint account found for that Google address.',
  google_not_configured: 'Google sign-in is not configured yet.',
};

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, setSession } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  useEffect(() => {
    const errKey = searchParams.get('error');
    if (errKey) setError(GOOGLE_ERROR_MESSAGES[errKey] || 'Sign-in failed. Please try again.');
  }, [searchParams]);

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setError('');
    try {
      const res = await fetch('/api/auth/google/url');
      const data = await res.json() as { success: boolean; url?: string; error?: string };
      if (!data.success || !data.url) {
        setError(data.error || 'Google sign-in is not configured yet.');
        return;
      }
      window.location.href = data.url;
    } catch {
      setError('Could not reach the server. Please try again.');
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(formData);
      navigate('/dashboard');
    } catch (err) {
      // #66 — clients/employees often forget the /portal path and sign in here.
      // Rather than a dead "Invalid credentials", try the portal login and send
      // them straight to their portal.
      try {
        const { portalLogin } = await import('@lib/api/portal');
        const r = await portalLogin(formData.email, formData.password);
        setSession(r.accessToken, r.refreshToken, r.user as unknown as Parameters<typeof setSession>[2]);
        navigate(r.user.role === 'employee' ? '/portal/employee' : '/portal/client', { replace: true });
        return;
      } catch { /* not a portal user either — show the original admin error */ }
      const error = err as { response?: { data?: { error?: string } }; message?: string };
      setError(error.response?.data?.error || error.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="material-symbols-outlined text-primary text-5xl">construction</span>
            <h1 className="text-4xl font-black text-black">OchrePoint</h1>
          </div>
          <p className="text-sm text-gray-600">Built on Skill, Finished with Pride</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-xl p-8 shadow-lg">
          <h2 className="text-2xl font-bold text-black mb-6">Sign In</h2>

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-error/10 border border-error text-error text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email Input */}
            <div>
              <label className="block text-sm font-medium text-gray-800 mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-gray-900 focus:outline-0 focus:ring-0 border border-gray-300 bg-gray-50 focus:border-primary h-11 placeholder:text-[#9dabb9] px-3 text-sm font-normal leading-normal"
                placeholder="you@example.com"
                required
              />
            </div>

            {/* Password Input */}
            <div>
              <label className="block text-sm font-medium text-gray-800 mb-2">
                Password
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-gray-900 focus:outline-0 focus:ring-0 border border-gray-300 bg-gray-50 focus:border-primary h-11 placeholder:text-[#9dabb9] px-3 text-sm font-normal leading-normal"
                placeholder="••••••••"
                required
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="flex w-full min-w-[84px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-11 px-4 bg-primary text-white text-sm font-bold leading-normal tracking-[0.015em] hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Signing in...</span>
                </div>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="mt-5 flex items-center gap-3">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-400 font-medium">or</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* Google Sign In */}
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={googleLoading || loading}
            className="mt-4 flex w-full items-center justify-center gap-2.5 rounded-lg h-11 border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {googleLoading ? (
              <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
            ) : (
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
            )}
            Sign in with Google
          </button>

          {/* Public demo — sandboxed in-browser, fake data, no server writes */}
          <a href="/dashboard?demo=admin"
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg h-11 border border-dashed border-[#C77D3F]/50 bg-[#C77D3F]/5 text-[#8a5526] text-sm font-semibold hover:bg-[#C77D3F]/10 transition-colors">
            <span className="material-symbols-outlined text-lg">play_circle</span>
            Explore the admin demo (fake data, nothing saved)
          </a>

          {/* Register Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <Link to="/register" className="text-primary font-medium hover:underline">
                Create account
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-gray-500">
            © 2026 OchrePoint Contracting LTD. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};


